import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Alert,
  Spinner,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch";

export default function JurnalTvaIesiriValuta() {
  const navigate = useNavigate();

  const today = new Date();
  const yyyy = today.getFullYear();

  const [from, setFrom] = useState(new Date(yyyy, 0, 1));
  const [to, setTo] = useState(today);
  const [q, setQ] = useState("");

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState({ rows: [], totals_by_cota: [] });
  const [loaded, setLoaded] = useState(false);

  const formatDateString = (date) => {
    if (!date) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const fmt2 = (n) =>
    Number(n ?? 0).toLocaleString("ro-RO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const fmt4 = (n) =>
    Number(n ?? 0).toLocaleString("ro-RO", {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    });

  const load = async () => {
    setBusy(true);
    setErr("");
    setLoaded(false);
    try {
      const fromStr = formatDateString(from);
      const toStr = formatDateString(to);
      const res = await authFetch(`/rapoarte/jurnal-tva-iesiri-valuta?from=${fromStr}&to=${toStr}`);
      setData({
        rows: Array.isArray(res?.rows) ? res.rows : [],
        totals_by_cota: Array.isArray(res?.totals_by_cota)
          ? res.totals_by_cota
          : [],
      });
      setLoaded(true);
    } catch (e) {
      setErr(e?.message || "Eroare la încărcare Jurnal TVA în valută.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    // don't auto load on mount
  }, []);

  const filteredRows = useMemo(() => {
    const rows = data.rows || [];
    const s = (q || "").trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => {
      const a = String(r.doc_no ?? "").toLowerCase();
      const b = String(r.partener ?? "").toLowerCase();
      const c = String(r.cui ?? "").toLowerCase();
      const d = String(r.valuta ?? "").toLowerCase();
      return a.includes(s) || b.includes(s) || c.includes(s) || d.includes(s);
    });
  }, [data.rows, q]);

  const grouped = useMemo(() => {
    const m = new Map();
    for (const r of filteredRows) {
      const k = r.doc_id;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(r);
    }
    const arr = Array.from(m.entries()).map(([doc_id, rows]) => {
      rows.sort((a, b) => Number(a.cota ?? 0) - Number(b.cota ?? 0));
      return { doc_id, rows };
    });

    arr.sort((A, B) => {
      const a0 = A.rows[0] || {};
      const b0 = B.rows[0] || {};
      const da = String(a0.doc_date || "");
      const db = String(b0.doc_date || "");
      if (da !== db) return da.localeCompare(db);
      return String(a0.doc_no || "").localeCompare(String(b0.doc_no || ""));
    });

    return arr;
  }, [filteredRows]);

  const totalsGeneral = useMemo(() => {
    const t = data.totals_by_cota || [];
    return {
      baza_l: t.reduce((s, x) => s + Number(x.baza_l || 0), 0),
      tva_l: t.reduce((s, x) => s + Number(x.tva_l || 0), 0),
      total_l: t.reduce((s, x) => s + Number(x.total_l || 0), 0),
    };
  }, [data.totals_by_cota]);

  return (
    <Card className="shadow-sm border-0 rounded-4">
      <Card.Body className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 className="fw-bold text-dark mb-1">Jurnal TVA Vânzări (în valută)</h4>
            <p className="text-muted small mb-0">
              Facturi de vânzare în valută străină pentru perioada selectată
            </p>
          </div>
          <div className="d-flex align-items-center gap-2 d-print-none">
            <Button variant="outline-primary" size="sm" className="fw-semibold shadow-sm me-2" onClick={() => window.print()}>
              <i className="bi bi-printer me-1"></i> Tipărește
            </Button>
            <div className="bg-light px-3 py-2 rounded-3 text-muted small">
              {filteredRows.length} poziții • {grouped.length} documente în valută
            </div>
          </div>
        </div>

        <Form className="bg-light p-3 rounded-4 mb-4 border border-light-subtle d-print-none">
          <Row className="align-items-end g-3">
            <Col xs={12} md={3}>
              <Form.Label className="fw-semibold text-secondary small mb-1">De la</Form.Label>
              <DatePicker
                selected={from}
                onChange={(date) => setFrom(date)}
                dateFormat="dd.MM.yyyy"
                className="form-control border-0 shadow-sm"
              />
            </Col>
            <Col xs={12} md={3}>
              <Form.Label className="fw-semibold text-secondary small mb-1">Până la</Form.Label>
              <DatePicker
                selected={to}
                onChange={(date) => setTo(date)}
                dateFormat="dd.MM.yyyy"
                className="form-control border-0 shadow-sm"
              />
            </Col>
            <Col xs={12} md={4}>
              <Form.Label className="fw-semibold text-secondary small mb-1">Căutare rapidă</Form.Label>
              <Form.Control
                placeholder="Factură / Partener / CUI / Valută"
                className="border-0 shadow-sm"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </Col>
            <Col xs={12} md={2}>
              <Button onClick={load} disabled={busy} className="w-100 fw-semibold shadow-sm">
                {busy ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Încărcare...
                  </>
                ) : (
                  "Reîncarcă"
                )}
              </Button>
            </Col>
          </Row>
        </Form>

        {err && (
          <Alert variant="danger" className="mt-3 mb-4 rounded-3 border-0 shadow-sm">
            {err}
          </Alert>
        )}

        <div className="table-responsive">
          {busy ? (
            <div className="py-5 text-center">
              <Spinner animation="border" variant="primary" />
              <div className="mt-2 text-muted small">Se procesează datele...</div>
            </div>
          ) : (
            <Table hover bordered responsive size="sm" className="align-middle rounded-3 overflow-hidden">
              <thead className="bg-primary text-white">
                <tr>
                  <th colSpan={7} className="text-center border-bottom-0 py-2 table-primary text-dark fw-bold">Document în Valută</th>
                  <th colSpan={3} className="text-center border-bottom-0 py-2 table-info text-dark fw-bold">Valori în Valută</th>
                  <th colSpan={3} className="text-center border-bottom-0 py-2 table-success text-dark fw-bold">Valori în Lei (RON)</th>
                </tr>
                <tr>
                  <th style={{ minWidth: 95 }}>Dată</th>
                  <th style={{ minWidth: 90 }}>Număr</th>
                  <th>Partener</th>
                  <th style={{ minWidth: 100 }}>CUI</th>
                  <th style={{ minWidth: 50 }}>Cotă</th>
                  <th style={{ minWidth: 60 }}>Valută</th>
                  <th className="text-end" style={{ minWidth: 80 }}>Curs</th>
                  <th className="text-end" style={{ minWidth: 100 }}>Bază Valută</th>
                  <th className="text-end" style={{ minWidth: 100 }}>TVA Valută</th>
                  <th className="text-end" style={{ minWidth: 100 }}>Total Valută</th>
                  <th className="text-end" style={{ minWidth: 110 }}>Bază Lei</th>
                  <th className="text-end" style={{ minWidth: 110 }}>TVA Lei</th>
                  <th className="text-end" style={{ minWidth: 110 }}>Total Lei</th>
                </tr>
              </thead>

              <tbody>
                {grouped.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="text-center text-muted py-5 bg-white">
                      {loaded
                        ? "Nu s-au găsit facturi în valută pentru perioada selectată."
                        : "Alegeți parametrii și apăsați \"Reîncarcă\"."}
                    </td>
                  </tr>
                ) : (
                  grouped.map(({ doc_id, rows }) => {
                    const first = rows[0] || {};
                    const subBazaV = rows.reduce((s, r) => s + Number(r.baza_v || 0), 0);
                    const subTvaV = rows.reduce((s, r) => s + Number(r.tva_v || 0), 0);
                    const subTotV = rows.reduce((s, r) => s + Number(r.total_v || 0), 0);
                    const subBazaL = rows.reduce((s, r) => s + Number(r.baza_l || 0), 0);
                    const subTvaL = rows.reduce((s, r) => s + Number(r.tva_l || 0), 0);
                    const subTotL = rows.reduce((s, r) => s + Number(r.total_l || 0), 0);

                    const span = rows.length + 1;

                    return (
                      <React.Fragment key={doc_id}>
                        {rows.map((r, idx) => (
                          <tr key={`${doc_id}-${r.cota}-${idx}`}>
                            {idx === 0 && (
                              <>
                                <td rowSpan={span} className="bg-white">{first.doc_date}</td>
                                <td rowSpan={span} className="bg-white">
                                  <span className="fw-semibold text-primary text-dark-print cursor-pointer" style={{ cursor: "pointer" }}
                                    onClick={() => navigate(`/iesire/${doc_id}/edit`)}
                                    title="Deschide factura"
                                  >
                                    {first.doc_no || ""}
                                  </span>
                                </td>
                                <td rowSpan={span} className="bg-white">{first.partener || "-"}</td>
                                <td rowSpan={span} className="bg-white">{first.cui || ""}</td>
                              </>
                            )}

                            <td className="text-end text-secondary fw-semibold">{Number(r.cota ?? 0)}%</td>
                            <td className="text-center font-monospace">{r.valuta}</td>
                            <td className="text-end font-monospace">{fmt4(r.curs)}</td>
                            <td className="text-end font-monospace">{fmt2(r.baza_v)}</td>
                            <td className="text-end font-monospace">{fmt2(r.tva_v)}</td>
                            <td className="text-end font-monospace">{fmt2(r.total_v)}</td>
                            <td className="text-end">{fmt2(r.baza_l)}</td>
                            <td className="text-end">{fmt2(r.tva_l)}</td>
                            <td className="text-end">{fmt2(r.total_l)}</td>
                          </tr>
                        ))}

                        <tr className="table-light fw-semibold">
                          <td colSpan={3} className="text-end bg-light small py-1 border-start-0">Subtotal {first.valuta}</td>
                          <td className="text-end bg-light small py-1 font-monospace">{fmt2(subBazaV)}</td>
                          <td className="text-end bg-light small py-1 font-monospace">{fmt2(subTvaV)}</td>
                          <td className="text-end bg-light small py-1 font-monospace">{fmt2(subTotV)}</td>
                          <td className="text-end bg-light small py-1">{fmt2(subBazaL)}</td>
                          <td className="text-end bg-light small py-1">{fmt2(subTvaL)}</td>
                          <td className="text-end bg-light small py-1">{fmt2(subTotL)}</td>
                        </tr>
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>

              <tfoot className="table-secondary border-top border-dark-subtle">
                {(data.totals_by_cota || []).map((t, idx) => (
                  <tr key={`tot-${t.cota}-${t.valuta}-${idx}`} className="border-0">
                    <td colSpan={4} className="border-0 bg-transparent"></td>
                    <td className="text-end fw-semibold bg-transparent border-start-0">
                      Total {Number(t.cota ?? 0)}%
                    </td>
                    <td className="text-center font-monospace">{t.valuta}</td>
                    <td className="border-0 bg-transparent"></td>
                    <td className="text-end fw-semibold font-monospace">{fmt2(t.baza_v)}</td>
                    <td className="text-end fw-semibold font-monospace">{fmt2(t.tva_v)}</td>
                    <td className="text-end fw-semibold font-monospace">{fmt2(t.total_v)}</td>
                    <td className="text-end fw-semibold">{fmt2(t.baza_l)}</td>
                    <td className="text-end fw-semibold">{fmt2(t.tva_l)}</td>
                    <td className="text-end fw-semibold">{fmt2(t.total_l)}</td>
                  </tr>
                ))}

                <tr className="table-dark text-white fw-bold">
                  <td colSpan={7} className="text-end py-2">TOTAL GENERAL LEI (RON)</td>
                  <td colSpan={3} className="text-end bg-transparent border-0"></td>
                  <td className="text-end py-2">{fmt2(totalsGeneral.baza_l)}</td>
                  <td className="text-end py-2">{fmt2(totalsGeneral.tva_l)}</td>
                  <td className="text-end py-2">{fmt2(totalsGeneral.total_l)}</td>
                </tr>
              </tfoot>
            </Table>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
